import {Button, Stack, Typography} from '@mui/material'
import {useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'
import MainBtn from '../customTable/MainBtn'
import {getOldestUnpaidDaysPastDue} from './getOldestUnpaidDaysPastDue'

const REMINDER_KEY = 'service_fees_reminder_dismissed'
const MS_PER_DAY = 1000 * 60 * 60 * 24

function getTodayIso(): string {
  const now = new Date()
  const y = now.getFullYear()
  const m = String(now.getMonth() + 1).padStart(2, '0')
  const d = String(now.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

export function isServiceFeesReminderActive(): boolean {
  const raw = localStorage.getItem(REMINDER_KEY)
  if (!raw) {
    return false
  }
  const dismissed = new Date(`${raw}T00:00:00`)
  if (Number.isNaN(dismissed.getTime())) {
    return false
  }
  const daysSince =
    (new Date().setHours(0, 0, 0, 0) - dismissed.setHours(0, 0, 0, 0)) / MS_PER_DAY
  return daysSince < 3
}

type Props = {
  rows: any[]
  unpaidTotal: number
  unpaidCurrency: string
  isPayingAll: boolean
  isWhishEnabled: boolean
  canPay: boolean
  onPayNow: () => void
  onDismiss?: () => void
}

export default function ServiceFeesDueCard({
  rows,
  unpaidTotal,
  unpaidCurrency,
  isPayingAll,
  isWhishEnabled,
  canPay,
  onPayNow,
  onDismiss,
}: Props) {
  const {t} = useTranslation()
  const [isDismissed, setIsDismissed] = useState(() => isServiceFeesReminderActive())

  const daysPastDue = useMemo(() => getOldestUnpaidDaysPastDue(rows), [rows])

  const shouldShow =
    !isDismissed && unpaidTotal > 0 && daysPastDue !== null && daysPastDue > 7

  if (!shouldShow) {
    return null
  }

  const formattedAmount = unpaidTotal.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
  const amountDisplay = `${(unpaidCurrency || '').trim()} ${formattedAmount}`.trim()

  const handleRemindLater = () => {
    localStorage.setItem(REMINDER_KEY, getTodayIso())
    setIsDismissed(true)
    onDismiss?.()
  }

  return (
    <Stack alignItems='center' mb='24px' mt='16px' width='100%'>
      <Stack
        width={{xs: '85%', sm: '55%', md: '42%', lg: '38%'}}
        maxWidth='480px'
        bgcolor='#2C3E6B'
        borderRadius='12px'
        p={{xs: '14px', sm: '18px', md: '28px'}}
        gap='12px'
        alignItems='center'
        textAlign='center'
      >
        <Typography color='#FFFFFF' fontSize='2.5rem' fontWeight={700} lineHeight={1.2}>
          {amountDisplay}
        </Typography>
        <Typography color='#FFFFFF' fontSize='14px' fontWeight={400}>
          {t('service_fees_overdue_days', {days: daysPastDue})}
        </Typography>
        <Stack
          direction='row'
          flexWrap='wrap'
          justifyContent='center'
          gap='12px'
          mt='8px'
          width='100%'
        >
          {canPay && isWhishEnabled && (
            <MainBtn
              width='auto'
              bgcolor='#FFFFFF'
              color='#2C3E6B'
              text='service_fees_pay_now'
              isLoading={isPayingAll}
              disabled={isPayingAll || unpaidTotal <= 0}
              handelSubmit={onPayNow}
            />
          )}
          <Button
            onClick={handleRemindLater}
            sx={{
              color: 'rgba(255,255,255,0.85)',
              border: '1px solid rgba(255,255,255,0.5)',
              textTransform: 'none',
              fontWeight: 600,
              px: 2,
            }}
          >
            {t('service_fees_remind_later')}
          </Button>
        </Stack>
      </Stack>
    </Stack>
  )
}
